import api from "./api";
import {
  mockPurchaseOrders,
  mockStockAdjustments,
  mockLowStockProducts,
  mockInventoryOverview,
  mockVendors,
} from "./mockInventoryService";

const inventoryService = {
  // Purchase Order APIs
  createPurchaseOrder: async (purchaseOrderData) => {
    const response = await api.post("/api/purchase-orders", purchaseOrderData);
    return response.data;
  },

  getPurchaseOrders: async (params = {}) => {
    try {
      const response = await api.get("/api/purchase-orders", { params });
      console.log("Purchase Orders API Response:", response.data);
      return response.data;
    } catch (error) {
      console.log(
        "Purchase Orders API failed, using mock data:",
        error.message
      );
      return mockPurchaseOrders;
    }
  },

  getPurchaseOrderById: async (id) => {
    const response = await api.get(`/api/purchase-orders/${id}`);
    return response.data;
  },

  updatePurchaseOrder: async (id, purchaseOrderData) => {
    const response = await api.put(
      `/api/purchase-orders/${id}`,
      purchaseOrderData
    );
    return response.data;
  },

  deletePurchaseOrder: async (id) => {
    const response = await api.delete(`/api/purchase-orders/${id}`);
    return response.data;
  },

  updatePurchaseOrderPaymentStatus: async (id, paymentStatus) => {
    const response = await api.patch(
      `/api/purchase-orders/${id}/payment-status`,
      {
        payment_status: paymentStatus,
      }
    );
    return response.data;
  },

  getPurchaseOrdersByVendor: async (vendorId) => {
    const response = await api.get(`/api/purchase-orders/vendor/${vendorId}`);
    return response.data;
  },

  getAvailableVendors: async () => {
    try {
      const response = await api.get("/api/purchase-orders/vendors/available");
      return response.data;
    } catch (error) {
      console.log(
        "Available vendors API failed, using mock data:",
        error.message
      );
      return mockVendors;
    }
  },

  // Purchase Items APIs
  createPurchaseItems: async (purchaseOrderId, items) => {
    const response = await api.post(
      `/api/purchase-orders/${purchaseOrderId}/items`,
      {
        items,
      }
    );
    return response.data;
  },

  getPurchaseItems: async (purchaseOrderId) => {
    const response = await api.get(
      `/api/purchase-orders/${purchaseOrderId}/items`
    );
    return response.data;
  },

  updatePurchaseItem: async (purchaseOrderId, itemId, itemData) => {
    const response = await api.put(
      `/api/purchase-orders/${purchaseOrderId}/items/${itemId}`,
      itemData
    );
    return response.data;
  },

  deletePurchaseItem: async (purchaseOrderId, itemId) => {
    const response = await api.delete(
      `/api/purchase-orders/${purchaseOrderId}/items/${itemId}`
    );
    return response.data;
  },

  // Stock Adjustment APIs
  createStockAdjustment: async (adjustmentData) => {
    const response = await api.post("/api/stock-adjustments", adjustmentData);
    return response.data;
  },

  getStockAdjustments: async (params = {}) => {
    try {
      const response = await api.get("/api/stock-adjustments", { params });
      console.log("Stock Adjustments API Response:", response.data);
      return response.data;
    } catch (error) {
      console.log(
        "Stock Adjustments API failed, using mock data:",
        error.message
      );
      return mockStockAdjustments;
    }
  },

  getStockAdjustmentById: async (id) => {
    const response = await api.get(`/api/stock-adjustments/${id}`);
    return response.data;
  },

  updateStockAdjustment: async (id, adjustmentData) => {
    const response = await api.put(
      `/api/stock-adjustments/${id}`,
      adjustmentData
    );
    return response.data;
  },

  deleteStockAdjustment: async (id) => {
    const response = await api.delete(`/api/stock-adjustments/${id}`);
    return response.data;
  },

  getStockAdjustmentSummary: async (params = {}) => {
    const response = await api.get("/api/stock-adjustments/summary", {
      params,
    });
    return response.data;
  },

  getStockAdjustmentsByProduct: async (productId) => {
    const response = await api.get(
      `/api/stock-adjustments/product/${productId}`
    );
    return response.data;
  },

  // Inventory Management APIs
  getInventoryOverview: async (params = {}) => {
    try {
      const response = await api.get("/api/inventory/overview", { params });
      return response.data;
    } catch (error) {
      console.log(
        "Inventory Overview API failed, using mock data:",
        error.message
      );
      return mockInventoryOverview;
    }
  },

  getInventoryReports: async (params = {}) => {
    const response = await api.get("/api/inventory/reports", { params });
    return response.data;
  },

  bulkStockUpdate: async (updates) => {
    const response = await api.post("/api/inventory/bulk-update", { updates });
    return response.data;
  },

  getLowStockProducts: async (threshold = 10) => {
    try {
      // Handle threshold: empty string or NaN becomes 0, otherwise use the value
      let validThreshold;
      if (threshold === '' || threshold === null || threshold === undefined) {
        validThreshold = 0;
      } else if (isNaN(threshold)) {
        validThreshold = 0;
      } else {
        validThreshold = threshold;
      }
      
      const response = await api.get("/api/inventory/low-stock", {
        params: { threshold: validThreshold },
      });
      console.log("Low Stock Products API Response:", response.data);
      return response.data;
    } catch (error) {
      console.log(
        "Low Stock Products API failed, using mock data:",
        error.message
      );
      return mockLowStockProducts;
    }
  },

  getInventoryHistory: async (productId, params = {}) => {
    const response = await api.get(`/api/inventory/history/${productId}`, {
      params,
    });
    return response.data;
  },

  // Product Inventory APIs
  updateProductStock: async (productId, stockData) => {
    const response = await api.patch(
      `/api/products/${productId}/stock`,
      stockData
    );
    return response.data;
  },

  getProductInventoryDetails: async (productId) => {
    const response = await api.get(`/api/products/${productId}/inventory`);
    return response.data;
  },
};

export default inventoryService;
